// Dispatch między dostępnymi API captchy. Tylko jeden skrypt jest ładowany per request
// (zależnie od Config.recaptchaType), więc sprawdzamy obecność globala.
async function fetchCaptchaToken(siteKey, action) {
    if (typeof window.Captcha4oc !== 'undefined') {
        return window.Captcha4oc.getToken(action);
    }
    if (typeof grecaptcha === 'undefined') {
        throw new Error('No captcha API loaded');
    }
    // Enterprise expose'uje API pod grecaptcha.enterprise, V3 pod samym grecaptcha.
    const api = typeof grecaptcha.enterprise !== 'undefined' ? grecaptcha.enterprise : grecaptcha;
    await new Promise((resolve) => api.ready(resolve));
    return api.execute(siteKey, { action });
}

function clearErrors(form) {
    form.querySelectorAll('.has-error').forEach((group) => group.classList.remove('has-error'));
    form.querySelectorAll('.form__error').forEach((error) => error.remove());
}

function markError(form, field, text) {
    const group = field.closest('.form__group') ?? field.closest('.form__checkbox');
    if (!group) return;

    group.classList.add('has-error');

    // Zgody nie mają miejsca na komunikat — sam `has-error` na labelu wystarcza.
    if (!text || !group.classList.contains('form__group')) return;

    const error = document.createElement('p');
    error.className = 'form__error';
    error.textContent = text;
    group.append(error);
}

function showGlobalError(form, text) {
    const error = document.createElement('p');
    error.className = 'form__error form__error--global';
    error.textContent = text;
    form.prepend(error);
}

function applyServerErrors(form, payload) {
    const global = payload?.global ?? [];
    if (global.length) {
        showGlobalError(form, global[0]);
    }

    Object.entries(payload?.fields ?? {}).forEach(([name, text]) => {
        const field = form.querySelector(`[name="${CSS.escape(name)}"]`);
        if (field) markError(form, field, text);
    });

    if (!global.length && !Object.keys(payload?.fields ?? {}).length) {
        showGlobalError(form, 'Nie udało się wysłać wiadomości. Spróbuj ponownie.');
    }
}

export function ContactForm() {
    document.addEventListener('DOMContentLoaded', () => {
        const forms = document.querySelectorAll('[data-contact-form]');
        if (!forms.length) return;

        forms.forEach((form) => {
            const fields = form.querySelectorAll('input, select, textarea');
            let sending = false;
            const ageGate = form.querySelector('[data-age-gate]');
            const marketingWrapper = form.querySelector('[data-marketing-consent-wrapper]');
            const marketingInput = marketingWrapper?.querySelector('input');

            if (ageGate && marketingWrapper && marketingInput) {
                ageGate.addEventListener('change', (event) => {
                    const isAdult = event.target?.dataset.ageGateOption === 'yes';

                    marketingWrapper.hidden = !isAdult;
                    marketingInput.disabled = !isAdult;
                    if (!isAdult) {
                        marketingInput.checked = false;
                    }
                });
            }

            // Reset stanu błędu w momencie gdy user zaczyna poprawiać pole.
            fields.forEach((field) => {
                const reset = () => {
                    const group = field.closest('.form__group') ?? field.closest('.form__checkbox');
                    group?.classList.remove('has-error');
                    group?.querySelector('.form__error')?.remove();
                };
                field.addEventListener('input', reset);
                field.addEventListener('change', reset);
            });

            // Walidacja klienta + (opcjonalnie) token reCAPTCHA V3, a potem submit fetchem —
            // bez przeładowania strony, więc thanks-message pojawia się w miejscu formularza.
            // Form ma `novalidate`, więc przeglądarka nie pokazuje popupów — sprawdzamy `validity`
            // i sypiemy `.has-error`. Token captchy fetchowany dopiero gdy walidacja przeszła,
            // żeby nie palić wywołań grecaptcha.execute przy formie z błędami.
            form.addEventListener('submit', async (event) => {
                event.preventDefault();
                if (sending) return;

                let firstInvalid = null;

                fields.forEach((field) => {
                    if (field.type === 'hidden' || field.disabled) return;
                    if (field.validity.valid) return;

                    markError(form, field, null);
                    if (!firstInvalid) firstInvalid = field;
                });

                if (firstInvalid) {
                    firstInvalid.focus({ preventScroll: false });
                    return;
                }

                const siteKey = form.dataset.captchaSiteKey;
                const action = form.dataset.captchaAction;
                const tokenInput = form.querySelector('input[name="_captchaToken"]');

                if (siteKey && action && tokenInput) {
                    try {
                        tokenInput.value = await fetchCaptchaToken(siteKey, action);
                    } catch (e) {
                        console.warn('Captcha token fetch failed', e);
                    }
                }

                sending = true;
                clearErrors(form);

                try {
                    const response = await fetch(form.action || window.location.href, {
                        method: 'POST',
                        body: new FormData(form),
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            Accept: 'application/json',
                        },
                    });
                    const payload = await response.json().catch(() => null);

                    if (response.ok && payload?.ok) {
                        form.reset();
                        clearErrors(form);
                        form.dispatchEvent(new CustomEvent('contact-form:success', { bubbles: true }));
                    } else {
                        applyServerErrors(form, payload);
                    }
                } catch (e) {
                    console.warn('Contact form submit failed', e);
                    showGlobalError(form, 'Nie udało się wysłać wiadomości. Spróbuj ponownie.');
                } finally {
                    if (tokenInput) tokenInput.value = '';
                    sending = false;
                }
            });
        });
    });
}
